import PBService, {PBServiceProps} from "@irati/utils/services"
import client from "@irati/utils/services/apollo"

import {
  changeUser,
  getCachedContentCards,
  initialize,
  setLogger,
  logCardClick,
  logCardImpressions,
  openSession,
  requestImmediateDataFlush,
  subscribeToSdkAuthenticationFailures,
  setSdkAuthenticationSignature,
  logCardDismissal,
  subscribeToContentCardsUpdates,
  removeSubscription,
  Card,
  requestContentCardsRefresh
} from "@vendor/braze"
import {getBrazeApiKey, isLoggingEnabled, sdkEndpoint} from "../utils/env"
import brazeToken, {brazeTokenKey} from "./mutations/brazeToken"
import {BrazeLogger} from "./brazeLogger"

interface Subscription {
  onCardUpdates: (cards: Card[]) => void
  userId: string
  enableLogging: boolean
}

class BrazeService extends PBService {
  logger = new BrazeLogger(isLoggingEnabled)
  loggedIn = false

  private updater?: Subscription["onCardUpdates"]

  constructor(props: PBServiceProps) {
    super(props)

    setLogger(this.logger.log)

    initialize(getBrazeApiKey(), {
      baseUrl: sdkEndpoint,
      doNotLoadFontAwesome: true,
      enableLogging: true,
      enableSdkAuthentication: true
    })
  }

  subscribe({onCardUpdates, userId, enableLogging}: Subscription) {
    this.logger.enableLogging(enableLogging)
    this.logger.debug("subscribing for user:", userId)
    this.updater = onCardUpdates

    const handler = subscribeToContentCardsUpdates((cards) => this.updater?.(cards.cards))
    this.logger.debug("handler", handler)

    if (!this.loggedIn) {
      this.setUserId(userId)
        .then(() => {
          openSession()
          onCardUpdates(getCachedContentCards().cards)
        })
        .catch((e) => {
          this.logger.error("error when setting id or opening session")
          this.logger.error(e)
        })
    } else {
      // docs still say to open session after subscribing
      openSession()
    }

    return handler
  }

  unSubscribe(handle: string) {
    this.logger.debug("unsubscribing", handle)
    this.updater = undefined
    removeSubscription(handle)
  }

  markCardAsClicked(id: string) {
    this.logger.log(`card clicked id: ${id}`)

    const cards = getCachedContentCards().cards
    const card = cards.find((c) => c.id === id)

    if (card) {
      mutateCard(card)
      logCardClick(card, true)

      this.flush([id])
    }
  }

  markCardsAsSeen(ids: string[]) {
    this.logger.log(`cards seen ids:${toBulletList(ids)}`)

    const cards = getCachedContentCards()
      .cards.filter((card) => ids.includes(card.id ?? ""))
      .map(mutateCard)

    if (cards.length) {
      logCardImpressions(cards, true)
      this.flush(ids)
    }
  }

  markCardsAsDismissed(ids: string[]) {
    this.logger.log(`card dismissed id:${toBulletList(ids)}`)

    getCachedContentCards()
      .cards.filter((card) => ids.includes(card.id ?? ""))
      .map(mutateCard)
      .forEach(logCardDismissal)

    this.flush(ids)
  }

  markCardsAsActioned() {
    this.logger.debug("actioning cards")
    this.updater?.(getCachedContentCards().cards)
  }

  refresh() {
    requestContentCardsRefresh(
      () => {},
      () => {
        this.logger.warn("failed to refresh cards")
      }
    )
  }

  private flush(ids: string[]) {
    requestImmediateDataFlush((wasFlushed) => {
      if (!wasFlushed) this.logger.warn(`failed to flush:${toBulletList(ids)}`)
    })
  }

  private async setUserId(userId: string) {
    if (this.loggedIn) return

    const jwtToken = await getBrazeJWT()
    changeUser(userId, jwtToken)

    subscribeToSdkAuthenticationFailures(async () => {
      const newJWT = await getBrazeJWT()
      setSdkAuthenticationSignature(newJWT)
    })

    this.loggedIn = true
  }
}

async function getBrazeJWT(): Promise<string> {
  const {data} = await client.mutate({
    mutation: brazeToken
  })
  return data[brazeTokenKey]
}

function toBulletList(items: string[]) {
  return `\n- ${items.join("\n- ")}`
}

function mutateCard(card: any) {
  // HACK needed to ensure these are set so braze marks them as seen
  card.url = card.extras?.component_id ? "IGNORE" : card.url
  return card
}

const brazeService = new BrazeService({errorHandlers: []})

export default brazeService
